import {PopulationSourceType, VariantDetails, DatasetInfo} from '@/generated/graphql'
import React, {useRef} from 'react'
import styled from 'styled-components'
import MuiTableContainer from '@mui/material/TableContainer'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {marks} from '@/components/detail/DetailTable/TableToolbar/TableFilters/AlleleFrequency/AlleleFrequency'
import {unscale} from '@/components/detail/DetailTable/TableToolbar/TableFilters/utils'

import TableFooter from '@mui/material/TableFooter'

import {
    TableCell as CoreTableCell,
    Table,
    TableHeadCell,
    TableSortLabel as CoreTableSortLabel, TablePopulationCellSubHeader,
} from '@/components/detail/DetailTable/TableComponents'
import VariantPagePopulationFrequenciesCellBar
    from '@/components/pages/VariantPage/VariantPagePopulationFrequencies/VariantPagePopulationFrequenciesCellBar'
import VariantPagePopulationFrequenciesHeadCellBar
    from '@/components/pages/VariantPage/VariantPagePopulationFrequencies/VariantPagePopulationFrequenciesHeadCellBar'
import {GNOMAD_POPULATION_NAMES} from '@/utils/populationNames'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import dashIfUndefined from '@/components/pages/VariantPage/dashIfUndefined'

const TableSortLabel = styled(CoreTableSortLabel)`
  &&.MuiTableSortLabel-root {
    font-weight: 400;
    text-align: left;
    justify-content: flex-end;

    svg {
      color: ${props => props.theme.palette.primary.main};
    }
  }

  && svg {
    transform: rotate(-90deg);
    opacity: 1;
  }

  &&.Mui-active svg {
    transform: rotate(0deg);
  }
`

const TableContainer = styled(MuiTableContainer)`
  margin-top: 40px;
  width: 100%;
  overflow-x: scroll;
`

const TableCell = styled(CoreTableCell)`
  && {
      color: ${ThemeColorsHospitalic.TEXT_PRIMARY};
    padding: 5px 0 5px 5px;
  }
`

const IndentedTableCell = styled(CoreTableCell)`
  && {
      color: ${ThemeColorsHospitalic.TEXT_PRIMARY};
    padding: 5px 0 5px 10px;
  }
`

type Props = {
    healthyVariant?: VariantDetails | null
    lungCancerVariant?: VariantDetails | null
    healthyDataset?: DatasetInfo
    lungCancerDataset?: DatasetInfo
}

export default function VariantPagePopulationFrequencies({healthyVariant, lungCancerVariant, healthyDataset, lungCancerDataset}: Props) {
    const ref = useRef<HTMLTableCellElement>(null)

    const countChartSize = (frequency: number) => {
        const value = unscale(frequency, marks)

        if (
            ref.current &&
            ref.current.offsetWidth &&
            value !== null &&
            value !== undefined
        ) {
            const containerWidth = ref.current.offsetWidth
            const containerWidthCounted = containerWidth - 50
            return (containerWidthCounted / 100) * value
        }
        return 20
    }

    const renderDatasetFrequencies = (
        variant: VariantDetails | null | undefined,
        datasetName: string
    ) => {
        if (!variant) {
            return (
                <React.Fragment>
                    <TableRow>
                        <TablePopulationCellSubHeader colSpan={12}>
                            Frequencies in {datasetName}
                        </TablePopulationCellSubHeader>
                    </TableRow>
                    <TableRow>
                        <IndentedTableCell colSpan={2} style={{fontStyle: 'italic', color: '#999'}}>
                            No data available
                        </IndentedTableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                    </TableRow>
                </React.Fragment>
            )
        }

        const population_frequencies = variant.population_frequencies

        // Filter to only show genome allele stats (not GNOMAD)
        const datasetFrequencies = population_frequencies?.filter(
            freq => freq?.population_source === PopulationSourceType.FrequenciesGenomeAlleleStats
        )

        if (!datasetFrequencies || datasetFrequencies.length === 0) {
            return (
                <React.Fragment>
                    <TableRow>
                        <TablePopulationCellSubHeader colSpan={12}>
                            Frequencies in {datasetName}
                        </TablePopulationCellSubHeader>
                    </TableRow>
                    <TableRow>
                        <IndentedTableCell colSpan={2} style={{fontStyle: 'italic', color: '#999'}}>
                            No data available
                        </IndentedTableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                        <TableCell style={{color: '#999'}}>—</TableCell>
                    </TableRow>
                </React.Fragment>
            )
        }

        return (
            <React.Fragment>
                {datasetFrequencies.map((freq, i) => (
                    <React.Fragment key={`${datasetName}_${i}`}>
                        <TableRow>
                            <TablePopulationCellSubHeader colSpan={12}>
                                Frequencies in {datasetName}
                            </TablePopulationCellSubHeader>
                        </TableRow>
                        {freq?.populations?.slice().sort((a, b) => {
                            // Define priority order: all, male, female first
                            const priorityOrder = ['all', 'male', 'female']
                            const aSubtype = a?.population_subtype || ''
                            const bSubtype = b?.population_subtype || ''

                            const aPriority = priorityOrder.indexOf(aSubtype)
                            const bPriority = priorityOrder.indexOf(bSubtype)

                            // If both are in priority list, sort by priority
                            if (aPriority !== -1 && bPriority !== -1) {
                                return aPriority - bPriority
                            }

                            // If only a is in priority list, it comes first
                            if (aPriority !== -1) {
                                return -1
                            }

                            // If only b is in priority list, it comes first
                            if (bPriority !== -1) {
                                return 1
                            }

                            // Both are not in priority list, sort alphabetically
                            return aSubtype.localeCompare(bSubtype)
                        }).map((pop, idx) => (
                            <TableRow key={`${datasetName}_${pop?.population_subtype}_${idx}`}>
                                <IndentedTableCell colSpan={2}>
                                    {pop?.population_subtype && GNOMAD_POPULATION_NAMES[pop.population_subtype as keyof typeof GNOMAD_POPULATION_NAMES]
                                        ? GNOMAD_POPULATION_NAMES[pop.population_subtype as keyof typeof GNOMAD_POPULATION_NAMES]
                                        : pop?.population_subtype}
                                </IndentedTableCell>
                                <TableCell>{pop?.allele_stats?.allele_count}</TableCell>
                                <TableCell>{pop?.allele_stats?.allele_number}</TableCell>
                                <TableCell>
                                    {dashIfUndefined((pop?.allele_stats?.allele_frequency) ? pop.allele_stats?.allele_frequency.toPrecision(4) : undefined)}
                                </TableCell>
                                <VariantPagePopulationFrequenciesCellBar
                                    size={(pop?.allele_stats?.allele_frequency) ? countChartSize(Number(pop.allele_stats?.allele_frequency.toPrecision(4))) : 0}
                                />
                            </TableRow>
                        ))}
                    </React.Fragment>
                ))}
            </React.Fragment>
        )
    }

    const renderGnomadFrequencies = () => {
        // Show GNOMAD frequencies from whichever variant is available
        const variant = healthyVariant || lungCancerVariant
        if (!variant) return null

        const population_frequencies = variant.population_frequencies
        const gnomadFrequencies = population_frequencies?.filter(
            freq => freq?.population_source === PopulationSourceType.FrequenciesGnomad
        )

        if (!gnomadFrequencies || gnomadFrequencies.length === 0) {
            return null
        }

        return (
            <React.Fragment>
                {gnomadFrequencies.map((freq, i) => (
                    <React.Fragment key={`gnomad_${i}`}>
                        <TableRow>
                            <TablePopulationCellSubHeader colSpan={12}>
                                Frequencies in GNOMAD
                            </TablePopulationCellSubHeader>
                        </TableRow>
                        {freq?.populations?.slice().sort((a, b) => {
                            // Define priority order: all, male, female first
                            const priorityOrder = ['all', 'male', 'female']
                            const aSubtype = a?.population_subtype || ''
                            const bSubtype = b?.population_subtype || ''

                            const aPriority = priorityOrder.indexOf(aSubtype)
                            const bPriority = priorityOrder.indexOf(bSubtype)

                            // If both are in priority list, sort by priority
                            if (aPriority !== -1 && bPriority !== -1) {
                                return aPriority - bPriority
                            }

                            // If only a is in priority list, it comes first
                            if (aPriority !== -1) {
                                return -1
                            }

                            // If only b is in priority list, it comes first
                            if (bPriority !== -1) {
                                return 1
                            }

                            // Both are not in priority list, sort alphabetically
                            return aSubtype.localeCompare(bSubtype)
                        }).map((pop, idx) => (
                            <TableRow key={`gnomad_${pop?.population_subtype}_${idx}`}>
                                <IndentedTableCell colSpan={2}>
                                    {pop?.population_subtype && GNOMAD_POPULATION_NAMES[pop.population_subtype as keyof typeof GNOMAD_POPULATION_NAMES]
                                        ? GNOMAD_POPULATION_NAMES[pop.population_subtype as keyof typeof GNOMAD_POPULATION_NAMES]
                                        : pop?.population_subtype}
                                </IndentedTableCell>
                                <TableCell>{pop?.allele_stats?.allele_count}</TableCell>
                                <TableCell>{pop?.allele_stats?.allele_number}</TableCell>
                                <TableCell>
                                    {dashIfUndefined((pop?.allele_stats?.allele_frequency) ? pop.allele_stats?.allele_frequency.toPrecision(4) : undefined)}
                                </TableCell>
                                <VariantPagePopulationFrequenciesCellBar
                                    size={(pop?.allele_stats?.allele_frequency) ? countChartSize(Number(pop.allele_stats?.allele_frequency.toPrecision(4))) : 0}
                                />
                            </TableRow>
                        ))}
                    </React.Fragment>
                ))}
            </React.Fragment>
        )
    }

    return (
        <TableContainer>
            <Table sx={{tableLayout: 'fixed', overflow: 'hidden'}}>
                <TableHead>
                    <TableRow>
                        <TableHeadCell colSpan={2}>Population</TableHeadCell>
                        <TableHeadCell>Allele Count</TableHeadCell>
                        <TableHeadCell>Allele Number</TableHeadCell>
                        <TableHeadCell>Allele Frequency</TableHeadCell>
                        <VariantPagePopulationFrequenciesHeadCellBar
                            containerWidth={575}
                        />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderDatasetFrequencies(healthyVariant, healthyDataset?.dataset_name || 'Healthy/apparently healthy')}
                    {renderDatasetFrequencies(lungCancerVariant, lungCancerDataset?.dataset_name || 'Lung cancer screening individuals')}
                    {renderGnomadFrequencies()}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className='right-align'></TableCell>
                        <TableCell className='right-align'></TableCell>
                        <TableCell></TableCell>
                        <VariantPagePopulationFrequenciesCellBar
                            ref={ref}
                            size={0}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}
